import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { patchItems } from '@/components/Patches/PatchSearchItems';
import { getItemPresentation, getMatchSnippet, highlightQueryParts } from '../searchBar/helpers';

const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  const parts = highlightQueryParts(text, query);
  if (parts.length === 0) return null;
  return (
    <>
      {parts.map((part, index) =>
        part.match ? (
          <mark key={`${part.text}-${index}`} className="search-dropdown-highlight">
            {part.text}
          </mark>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        ),
      )}
    </>
  );
};

type SearchSpotlightItemProps = {
  item: any;
  query: string;
  isFocused: boolean;
  isSelected: boolean;
  optionId: string;
  navIndex: number;
  onToggle: (item: any) => void;
  onGoTo: (item: any) => void;
};

export function SearchSpotlightItem({
  item,
  query,
  isFocused,
  isSelected,
  optionId,
  navIndex,
  onToggle,
  onGoTo,
}: SearchSpotlightItemProps) {
  const patched = patchItems(item);
  const presentation = getItemPresentation(patched);
  const matchSnippet = getMatchSnippet(patched);

  return (
    <li
      id={optionId}
      role="option"
      aria-selected={isSelected}
      data-nav-index={navIndex}
      data-testid={`search-spotlight-option-${navIndex}`}
      className={`search-dropdown-item search-spotlight-item${isFocused ? ' is-focused' : ''}${isSelected ? ' is-selected' : ''}`}
      onMouseDown={event => event.preventDefault()}
      onClick={() => onToggle(item)}
    >
      <input
        type="checkbox"
        className="search-dropdown-item__checkbox"
        checked={isSelected}
        readOnly
        tabIndex={-1}
        aria-hidden
      />
      <div className="search-dropdown-item__body">
        <div className="search-dropdown-item__title">
          <HighlightedText text={presentation.title} query={query} />
        </div>
        {presentation.subtitle ? (
          <div className="search-dropdown-item__subtitle">{presentation.subtitle}</div>
        ) : null}
        {(presentation.tags.length > 0 || matchSnippet) && (
          <div className="search-dropdown-item__meta">
            {presentation.tags.length > 0 && (
              <div className="search-dropdown-item__tags">
                {presentation.tags.map(tag => (
                  <span key={tag} className="search-dropdown-item__tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {matchSnippet ? <span className="search-dropdown-item__match">{matchSnippet}</span> : null}
          </div>
        )}
      </div>
      <button
        type="button"
        className="search-dropdown-item__arrow-btn"
        aria-label={`Search ${presentation.title || patched.id}`}
        title="Open this result now"
        data-testid={`search-spotlight-go-${navIndex}`}
        onMouseDown={event => event.preventDefault()}
        onClick={event => {
          event.stopPropagation();
          onGoTo(item);
        }}
      >
        <ArrowRightIcon className="search-dropdown-item__arrow" aria-hidden />
      </button>
    </li>
  );
}
